import { get, sumBy, values } from 'lodash';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { bcApi } from '../helpers/bigcommerce';
import AuthContext from './AuthProvider';
/* SUPPORT FOR LOCALISR AND CnC */
import { useToast } from '@chakra-ui/react';
import useSegment from 'hooks/useSegment';
import { useCartStore } from 'stores/cart';
import { useGlobalStore } from 'stores/global';
import { dataLayerPush, getStorage, setStorage } from '../helpers/general';
import { delay } from 'common/util';
// import { LOCAL_STORE, parseJsonValue } from '../components/molecules/Localisr/Statics';

const CartContext = createContext();

const initialState = {
  cartLoading: false,
  cartError: false,
  cart: {
    currency: {
      code: 'USD'
    },
    cartAmount: 0,
    lineItems: {},
    numberItems: 0,
    redirectUrls: {}
  },
  shippingMethod: 'delivery'

  /* SUPPORT FOR LOCALISR AND CnC */

  // shippingMethod: getStorage(LOCAL_STORE.SHIPPING_METHOD) || '',
  // selectedStore: {
  //   store_id: getStorage(LOCAL_STORE.UID) || '',
  //   store_name: getStorage(LOCAL_STORE.NAME) || '',
  //   store_address: getStorage(LOCAL_STORE.ADDRESS) || '',
  //   store_location: parseJsonValue(getStorage(LOCAL_STORE.LOCATION)) || false,
  // },
  // stockLoading: false,
  // stockAvailability: []
};

const getItemsIdInCart = lineItems => {
  return values(lineItems)
    .reduce((acc, curr) => {
      return [...acc, ...curr.map(ite => ({ item_id: ite.id, quantity: ite.quantity }))];
    }, [])
    .filter(x => x);
};

export const CartProvider = ({ children }) => {
  const setFrequency = useGlobalStore(state => state.setFrequency);
  const toast = useToast();
  const {
    setCartModal,
    setUpsellAction,
    setPrescription,
    setService,
    setUpdatingCartItem,
    setGiftModal
  } = useCartStore(state => state);
  const { track } = useSegment();
  const auth = useContext(AuthContext);
  const customer = auth && auth.state;
  const [state, setState] = useState(initialState);

  //use
  const fetchCart = async (cartId = '', cartFetched = false) => {
    setState(prev => ({
      ...prev,
      cartFetched
    }));
    return await bcApi('carts', 'GET', null, null, cartId)
      .then(({ response, status }) => {
        return refreshCart({ ...response, status });
      })
      .catch(error => {
        setState(prev => ({
          ...prev,
          cartLoading: false,
          cartFetched,
          cartError: error
        }));
        return false;
      });
  };

  useEffect(() => {
    const hasRecoveryToken = new URLSearchParams(window?.location?.search)?.has('t');
    if (hasRecoveryToken) return;
    fetchCart();
  }, []);

  const calculateNumberItems = lineItems => {
    const {
      physical_items = [],
      digital_items = [],
      custom_items = [],
      gift_certificates = []
    } = lineItems || {};
    const numberPhysical = sumBy(physical_items, ite => ite.quantity) || 0;
    const numberDigital = sumBy(digital_items, ite => ite.quantity) || 0;
    const numberCustom = sumBy(custom_items, ite => ite.quantity) || 0;
    const numberGift = sumBy(gift_certificates, ite => ite.quantity) || 0;
    return numberPhysical + numberDigital + numberCustom + numberGift;
  };
  //use

  const updateState = async response => {
    const {
      line_items: lineItems,
      cart_amount: cartAmount,
      base_amount: baseAmount,
      currency,
      id: cartId,
      coupons,
      redirect_urls
    } = response.data || {};
    // if response empty then empty cart
    let newState = { ...initialState, cartLoading: false, cartFetched: true };

    if (lineItems && lineItems.physical_items && cartId) {
      const productIds = lineItems?.physical_items.map(product => product.product_id);
      const productData = await bcApi(
        `catalog/products?include=variants,modifiers,custom_fields,options,images&id:in=${productIds.join(
          ','
        )}`
      );

      if (!productData?.response || productData.status !== 200) {
        setState(prev => ({
          ...prev,
          cartLoading: false,
          updatingItem: false
        }));
        return state;
      }

      const productInfo = productData.response.data.reduce((info, product) => {
        info[product.id] = product;
        return info;
      }, {});
      const [autoShipProducts, activeAutoshipProducts] = lineItems?.physical_items.reduce(
        ([shipProducts, activeProducts], item) => {
          const findModifier = productData.response.data.find(
            product =>
              product.id === item.product_id &&
              product.modifiers.find(field => field.name === 'Ships Every')
          );
          if (findModifier) shipProducts.push({ ...item, details: findModifier });
          const findAutoshipOption = item.options.find(option => option.name === 'Ships Every');
          if (findAutoshipOption) activeProducts.push(item.id);
          return [shipProducts, activeProducts];
        },
        [[], []]
      );

      const frequencyOptions =
        autoShipProducts.length > 0
          ? autoShipProducts[0].details?.modifiers.find(field => field.name === 'Ships Every')
              .option_values
          : [];

      newState = {
        ...state,
        cartFetched: true,
        cartLoading: false,
        updatingItem: false,
        coupons,
        cart: {
          cartId,
          currency,
          cartAmount,
          lineItems,
          productInfo,
          autoShipProducts,
          activeAutoshipProducts,
          frequencyOptions,
          baseAmount,
          numberItems: calculateNumberItems(lineItems),
          redirectUrls: redirect_urls || state.cart.redirectUrls
        }
      };
    }
    await refreshCheckout(cartId, newState);
    return newState;
  };
  //use

  const refreshCart = response => {
    if (response?.status === 204 || response?.status === 404) {
      setState(prev => ({
        ...prev,
        coupons: response.data?.coupons,
        cartLoading: false,
        cartFetched: true,
        // Reset cart to empty state for 404
        cart: {
          ...initialState.cart,
          cartId: prev.cart.cartId
        }
      }));
    } else {
      return updateState(response.response ?? response);
    }
  };

  const refreshCheckout = async (cartId, theState) => {
    let currentState = theState;
    return new Promise(resolve => {
      bcApi(`checkouts/${cartId}`).then((response, status) => {
        if (status === 200) {
          currentState = { ...theState, checkout: response.data };
        }

        setState(currentState);
        setFrequency(false);
        setService(false);
        setUpdatingCartItem(false);
        resolve(currentState);
      });
    });
  };

  const initCheckout = async () => {
    const { response, status } = await bcApi(`checkouts/${state.cart.cartId}`);
    if (status === 200) {
      setState(prev => ({
        ...prev,
        coupons: response.data.coupons,
        checkout: response.data
      }));

      return { ...state, checkout: response.data };
    }
    return state;
  };

  const replaceCartItem = (lineObject, lineItemIds) => {
    return new Promise(async (resolve, reject) => {
      setFrequency(true);
      const bcApiBody = JSON.stringify(lineObject);
      try {
        let lastResponse;
        // Delete line items one by one
        for (const lineItemId of lineItemIds) {
          lastResponse = await bcApi(`carts/${state.cart.cartId}/items/${lineItemId}`, 'DELETE');
        }

        const { response, status } = await bcApi('carts/items', 'POST', bcApiBody);
        if (status === 404 || status === 422) {
          const newCartBody = {};
          if (customer.isLoggedIn) {
            newCartBody.customer_id = customer.customerId;
          }
          //trying recreate new cart
          await bcApi('carts', 'POST', newCartBody);
          lastResponse = await bcApi('carts/items', 'POST', bcApiBody);
        } else {
          lastResponse = response;
        }
        const newCart = await updateState(lastResponse?.response ?? lastResponse);
        resolve(newCart?.cart?.activeAutoshipProducts);
      } catch (error) {
        console.error('Error in the process:', error);
        reject(error);
      } finally {
        setFrequency(false);
      }
    });
  };

  const addToCart = async ({
    productId,
    variantId,
    retry,
    quantity = 1,
    options = [],
    skipCartModal = false,
    giftProductId = 0,
    isGift = false,
    listPrice = false
  }) => {
    setFrequency(true);
    return new Promise((res, rej) => {
      setState(prev => ({
        ...prev,
        addingToCart: productId
      }));
      const lineItems = [
        {
          quantity: parseInt(quantity, 10),
          product_id: parseInt(productId, 10)
        }
      ];

      if (options.length > 0) {
        lineItems[0].option_selections = options;
      } else {
        lineItems[0].variant_id = parseInt(variantId, 10);
      }

      //for custom price (Was add for member plan)
      if (listPrice) {
        lineItems[0].list_price = listPrice;
      }

      const cartBody = {
        line_items: lineItems
      };

      const bcApiBody = JSON.stringify(cartBody);

      bcApi('carts/items', 'POST', bcApiBody)
        .then(({ response, status }) => {
          if (status === 404 && !retry) {
            // cart was not found -> cart Id is deleted from cookie, secornd try will create new cart
            const addToCartBody = {
              productId: productId,
              variantId: variantId,
              retry: true,
              quantity: quantity,
              options: options,
              giftProductId: giftProductId,
              isGift: isGift
            };
            if (customer.isLoggedIn) {
              addToCartBody.customer_id = customer.customerId;
            }
            return addToCart(addToCartBody);
          }

          if (!response?.data && response.status > 300 && 'title' in response) {
            setState(prev => ({
              ...prev,
              addingToCart: false
            }));
            const message = response.title || 'Unable to add item to cart!';
            toast({
              description: message,
              status: 'error',
              position: 'top-right'
            });
            return;
          }

          if (response?.data) {
            // If we have a logged in customer and the cart is not bound to that customer, bind it now
            if (response.data.customer_id !== customer.customerId) {
              bcApi('carts', 'PUT', { customer_id: customer.customerId }).then(response => {
                refreshCart(response);
              });
            }

            const addedProduct = [];
            const itemTypes = Object.keys(response.data.line_items);
            const currentCart = response.data.line_items.physical_items || [];
            itemTypes.map(itemType => {
              response.data.line_items[itemType].map(item => {
                if (item.product_id === productId) {
                  addedProduct.push({
                    id: `${item.product_id}_${item.variant_id}`,
                    title: item.name,
                    price: item.sale_price || item.price || 0,
                    quantity: item.quantity,
                    isAutoship: item.options.some(option => option.name === 'Ships Every')
                  });
                }
                return true;
              });
              return true;
            });
            dataLayerPush('AddedToCart', null, addedProduct);
            updateState(response).then(() => {
              res(true);
              // check if we have a gift in cart yet
              if (giftProductId && currentCart && currentCart.length > 0) {
                const giftItem = currentCart.find(
                  item =>
                    Number(item.product_id) === Number(giftProductId) &&
                    !item.options.some(option => option.name === 'Ships Every')
                );
                if (!giftItem) {
                  setGiftModal(true);
                } else {
                  setCartModal(productId);
                }
              }

              if (!skipCartModal && !giftProductId && !isGift) setCartModal(productId);
              setUpsellAction(false);
            });

            setPrescription(false);
            setState(prev => ({
              ...prev,
              addingToCart: false
            }));
          } else {
            setState(prev => ({
              ...prev,
              addingToCart: false
            }));
          }
        })
        .catch(error => {
          setState(prev => ({
            ...prev,
            addingToCart: false
          }));
          const message = error.message || 'Unable to add item to cart!';
          toast({
            description: message,
            status: 'error',
            position: 'top-right'
          });
        })
        .finally(() => {
          setFrequency(false);
          setService(false);
        });
    });
  };

  const addAllToCart = (items, retry) => {
    return new Promise((res, rej) => {
      const lineItems = items
        .filter(product => {
          const productId = 'product_id' in product ? product.product_id : product.productId;
          return productId ? productId > 0 : parseInt(product[0], 10) > 0;
        })
        .map(product => {
          const productId = product.product_id || product.productId;
          const variantId = product.variant_id || product.variantId;

          if (productId) {
            return {
              quantity: 'quantity' in product ? parseInt(product.quantity, 10) : 1,
              product_id: parseInt(productId, 10),
              variant_id: parseInt(variantId, 10)
            };
          } else {
            return {
              quantity: 1,
              product_id: parseInt(product[0], 10),
              variant_id: parseInt(product[1], 10)
            };
          }
        });
      if (lineItems.length > 0) {
        const bcApiBody = JSON.stringify({
          line_items: lineItems
        });
        bcApi('carts/items', 'POST', bcApiBody)
          .then(({ response, status }) => {
            if (status === 404 && !retry) {
              // re create a cart if cart was destroyed
              return bcApi('carts').then(() => addAllToCart(items, true));
            }

            if (status === 422) {
              toast({
                description: 'Insufficient item stock',
                status: 'error',
                position: 'top-right'
              });
              return;
            }

            const addedProduct = [];
            const itemTypes = Object.keys(response.data.line_items);
            itemTypes.map(itemType => {
              response.data.line_items[itemType].map(item => {
                if (lineItems.find(product => item.product_id === product.product_id)) {
                  addedProduct.push({
                    id: `${item.product_id}_${item.variant_id}`,
                    title: item.name,
                    price: item.sale_price || item.price || 0,
                    quantity: item.quantity
                  });
                }
                return true;
              });
              return true;
            });
            dataLayerPush('AddedToCart', null, addedProduct);
            updateState(response).then(() => {
              res(true);
            });
          })
          .catch(error => {
            setState(prev => ({
              ...prev,
              addingToCart: false
            }));
            toast({
              description: 'Insufficient item stock',
              status: 'error',
              position: 'top-right'
            });
          });
      } else {
        setState(prev => ({
          ...prev,
          addingToCart: false
        }));
        toast({
          description: 'No products to add',
          status: 'error',
          position: 'top-right'
        });
      }
    });
  };

  const updateItemInCart = async (itemId, updatedItemData) => {
    const bcApiBody = JSON.stringify(updatedItemData);
    await bcApi(`carts/${state.cart.cartId}/items/${itemId}`, 'PUT', bcApiBody)
      .then(async (response, status) => {
        // delay waiting the state set from updateCartItemQuantity
        await delay(1000);
        if (status === 422) {
          toast({
            description: 'Insufficient item stock',
            status: 'error',
            position: 'top-right'
          });
          fetchCart();
        } else {
          refreshCart({ ...response, status: response.status });
        }
      })
      .catch(error => {
        setState(prev => ({ ...prev, cartLoading: false, cartError: error }));
      })
      .finally(() => {
        setUpdatingCartItem(false);
        setState(prev => ({
          ...prev,
          addingToCart: false,
          updatingItem: false
        }));
      });
  };

  const removeItemFromCart = async itemId => {
    setState(state => {
      return {
        ...state,
        updatingItem: itemId
      };
    });

    setFrequency(true);
    return new Promise(async res => {
      const removedProduct = [];
      const currentCart = { ...state.cart };
      const itemTypes = Object.keys(state.cart.lineItems);
      const productInfo = state.cart.productInfo || {};

      for (const itemType of itemTypes) {
        for (const item of currentCart.lineItems[itemType]) {
          if (item.id === itemId) {
            const product = productInfo[item.product_id];
            const variant = product?.variants?.find(v => v.id === item.variant_id);

            track('Product Removed', {
              product_id: product?.id,
              name: product?.name,
              sku: product?.sku,
              url: product?.custom_url?.url,
              image_url: product?.images?.[0]?.url_standard,
              price: item?.list_price,
              category: product?.categories?.join(', '),
              currency: 'AUD',
              variant: `${
                product?.variants?.length > 1
                  ? variant?.option_values?.[0]?.label
                  : 'Default Variant'
              } (${item?.variant_id})`,
              brand: product?.brand_id,
              quantity: item?.quantity
            });

            removedProduct.push(itemId);

            const giftField = product?.custom_fields?.find(
              f => f.name === 'giftProductId' && f.value !== ''
            );

            if (giftField) {
              const giftProduct = productInfo[Number(giftField.value)];
              const isGift = giftProduct?.custom_fields?.some(
                f => f.name === 'isGift' && f.value === 'true'
              );

              if (isGift) {
                const giftInCart = currentCart.lineItems.physical_items.find(
                  i => i.product_id === Number(giftField.value)
                );
                if (giftInCart) {
                  removedProduct.push(giftInCart.id);
                }
              }
            }
          }
        }
      }
      try {
        for (const item of removedProduct) {
          await bcApi(`carts/${currentCart.cartId}/items/${item}`, 'DELETE');
        }

        setState(prev => ({
          ...prev,
          cartFetched: true
        }));
      } catch (error) {
        setState(prev => ({
          ...prev,
          cartLoading: false,
          cartError: error
        }));
      } finally {
        console.log('removedProduct', removedProduct);
        await fetchCart('', true);
        setService(false);
        setUpdatingCartItem(false);
        setFrequency(false);
        res(true);
      }
    });
  };

  const updateCartItemQuantity = async (item, action) => {
    let newQuantity;
    if (['minus', 'plus'].indexOf(action) > -1) {
      newQuantity = item.quantity + (action === 'minus' ? -1 : 1);
    } else {
      newQuantity = action;
    }
    setState(prev => ({ ...prev, updatingItem: item.id }));

    // this now disabale action
    // if (newQuantity < 1) {
    //   return removeItemFromCart(item.id);
    // }
    let productVariantReferences = null;

    if (typeof item.product_id !== 'undefined') {
      productVariantReferences = {
        product_id: item.product_id,
        variant_id: item.variant_id
      };
    }
    await updateItemInCart(item.id, {
      line_item: {
        quantity: newQuantity,
        ...productVariantReferences
      }
    });
  };

  const addCoupons = async couponCode => {
    const rejectData = {
      description: 'Unable to apply coupon',
      status: 'error',
      position: 'top-right'
    };
    return new Promise(async (res, rej) => {
      const endpoint = `checkouts/${state.cart.cartId}/coupons`;
      const reqBody = { coupon_code: couponCode };
      try {
        const { response, status } = await bcApi(endpoint, 'POST', reqBody);
        const coupons = get(response, 'data.coupons');
        if (status === 200 && coupons) {
          const newCart = await bcApi('carts', 'GET');
          refreshCart({ ...newCart.response, status });
          res(newCart.response.data.coupons);
        } else {
          toast(rejectData);
        }
      } catch (error) {
        setState({ ...state, cartLoading: false, cartError: error });
        toast(rejectData);
      }
    });
  };

  const removeCoupons = async coupon_code => {
    const endpoint = `checkouts/${state.cart.cartId}/coupons/${coupon_code}`;
    try {
      const { response, status } = await bcApi(endpoint, 'DELETE');
      const coupons = get(response, 'data.coupons');
      if (status === 200 && coupons) {
        setState(prev => ({
          ...prev,
          coupons
        }));
        const newCart = await bcApi('carts', 'GET');
        refreshCart({ ...newCart.response, status });
        return coupons;
      } else {
        return response;
      }
    } catch (error) {
      setState(prev => ({ ...prev, cartLoading: false, cartError: error }));
      return error;
    }
  };

  const addConsignments = async shippingAddress => {
    const { cartId, lineItems } = state.cart;
    if (cartId) {
      const endpoint = `checkouts/${cartId}/consignments`;
      const req_body = [
        {
          shipping_address: shippingAddress,
          line_items: getItemsIdInCart(lineItems)
        }
      ];

      try {
        const { response, status } = await bcApi(endpoint, 'POST', req_body);
        if (status === 200) {
          setState(prev => ({ ...prev, consignments: response.data.consignments }));
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const updateConsignments = async (shippingAddress, consignmentId) => {
    const {
      cart: { cartId, lineItems }
    } = state;
    if (cartId) {
      const endpoint = `checkouts/${cartId}/consignments/${consignmentId}`;
      const req_body = {
        shipping_address: shippingAddress,
        line_items: getItemsIdInCart(lineItems)
      };
      try {
        const { status } = await bcApi(endpoint, 'PUT', req_body);
        if (status === 200) {
          setState(prev => ({ ...prev, consignments: [] }));
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const removeConsignments = async _state => {
    const { response, status } = await bcApi(`checkouts/${state.cart.cartId}`);
    if (status === 200) {
      const _consignments = response.data.consignments || [];
      if (_consignments && typeof _consignments !== 'undefined' && _consignments.length) {
        const endpoint = `checkouts/${state.cart.cartId}/consignments/${_consignments[0].id}`;
        const resRemoved = await bcApi(endpoint, 'DELETE');
        if (resRemoved.status === 200) {
          setState({
            ..._state,
            consignments: resRemoved.response.data.consignments || [],
            checkout: resRemoved.response.data
          });
        }
      }
    }
  };

  const addGiftCertificates = async giftCertCode => {
    const { cartId } = state.cart;
    if (cartId) {
      const endpoint = `checkouts/${cartId}/gift-certificates`;
      // TODO: make this request to work
      try {
        const { response, status } = await bcApi(
          endpoint,
          'POST',
          {
            giftCertificateCode: giftCertCode
          },
          'sf'
        );
        console.log(status, response);
      } catch (error) {
        console.log(error);
      }
    }
  };

  const changeShippingMethod = async (_method, _state = false) => {
    /* SUPPORT FOR LOCALISR AND CnC */
    // setStorage(LOCAL_STORE.SHIPPING_METHOD, _method);
    const _newState = { ...(_state ? _state : state), shippingMethod: _method };
    setState(_newState);

    if (_method === 'collect') {
      const result = await setPickupConsignment(_newState);
      if (result === 'forceDelivery') {
        await removeConsignments({ ..._newState, shippingMethod: 'delivery' });
        storeCheckoutData({ shipping: {} });
        return 'forceDelivery';
      } else {
        return true;
      }
    } else {
      await removeConsignments(_newState);
      storeCheckoutData({ shipping: {} });
      return true;
    }
  };

  const changeSelectedStore = _store => {
    /* SUPPORT FOR LOCALISR AND CnC */
    // store addresses
    // setStorage(LOCAL_STORE.UID, _store.store_id);
    // setStorage(LOCAL_STORE.NAME, _store.store_name);
    // setStorage(LOCAL_STORE.ADDRESS, _store.store_address);
    // setStorage(LOCAL_STORE.LOCATION, JSON.stringify(_store.store_location));

    // update state
    setState(prev => ({ ...prev, selectedStore: _store }));
  };

  const storeCheckoutData = obj => {
    const storeKey = '__jammcd';
    const existingJSON = getStorage(storeKey) || JSON.stringify({});
    const existing = JSON.parse(existingJSON);
    const newSet = JSON.stringify({ ...existing, ...obj });
    setStorage(storeKey, newSet);
  };

  const setPickupConsignment = async _state => {
    await removeConsignments(_state);

    const { cartId, lineItems } = _state.cart;
    const { selectedStore } = _state;
    const endpoint = `checkouts/${cartId}/consignments?include=consignments.available_shipping_options`;

    if (lineItems.physical_items.length === 0) {
      // Cart can not be collected
      return 'forceDelivery';
    }

    if (`${selectedStore.store_name}`.trim() === '') {
      return false;
    }

    try {
      const resAddConsignment = await bcApi(endpoint, 'POST', [
        {
          shipping_address: {
            first_name: selectedStore.store_name,
            last_name: selectedStore.store_id,
            address1: selectedStore.store_location.address_1,
            city: selectedStore.store_location.suburb,
            phone: selectedStore.store_location.phone,
            email: selectedStore.store_location.email,
            postal_code: selectedStore.store_location.postcode,
            state_or_province: selectedStore.store_location.state,
            country_code: selectedStore.store_location.country
          },
          line_items: getItemsIdInCart(lineItems)
        }
      ]);
      if (
        resAddConsignment.status === 200 &&
        resAddConsignment.response.data.consignments.length > 0
      ) {
        const _pickupOption =
          resAddConsignment.response.data.consignments[0].available_shipping_options.filter(
            a =>
              ['click', 'collect', 'pickup'].filter(b => a.description.toLowerCase().includes(b))
                .length > 0
          );
        if (_pickupOption.length) {
          const endpoint = `checkouts/${state.cart.cartId}/consignments/${resAddConsignment.response.data.consignments[0].id}`;
          const resUpdatedCheckout = await bcApi(endpoint, 'PUT', {
            shipping_option_id: _pickupOption[0].id
          });
          if (resUpdatedCheckout.status === 200) {
            setState({
              ..._state,
              consignments: resAddConsignment.response.data.consignments,
              checkout: resUpdatedCheckout.response.data
            });
            storeCheckoutData({
              shipping: resUpdatedCheckout.response.data.consignments[0].shipping_address
            });
            return true;
          }
          return false;
        } else {
          // Preselected store - cart can not be collected
          return 'forceDelivery';
        }
      } else {
        // Preselected store - cart can not be collected
        return 'forceDelivery';
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const loadingStock = loading => {
    setState(prev => ({ ...prev, stockLoading: loading }));
  };

  const updateStockAvailability = async _stocks => {
    setState(prev => ({ ...prev, stockAvailability: _stocks }));
  };

  return (
    <CartContext.Provider
      value={{
        state,
        addToCart,
        addAllToCart,
        addCoupons,
        removeCoupons,
        removeItemFromCart,
        updateCartItemQuantity,
        updateItemInCart,
        replaceCartItem,
        addConsignments,
        addGiftCertificates,
        updateConsignments,
        changeShippingMethod,
        changeSelectedStore,
        loadingStock,
        updateStockAvailability,
        initCheckout,
        fetchCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
